import React, { FC, useRef, useMemo } from 'react';
import { HorizontalGaugeBarSeries } from '../../interfaces/Charts';
import { useResize } from './hooks/useResize';
import { scaleLinear } from 'd3-scale';
import styled from '@emotion/styled';
import { VerticalLine } from './shapes/VerticalLine';
import {
  GAUGE_CHART_RECT_HEIGHT,
  GAUGE_CHART_VERTICAL_PADDING,
  GAUGE_CHART_RECT_PADDING,
} from './helpers/config';
import { format } from 'd3-format';

interface HorizontalGaugeBarChartProps {
  series: HorizontalGaugeBarSeries[];
  height?: number;
  width?: number;
}

const transformSeriesToRects = (series: HorizontalGaugeBarSeries[], width: number) => {
  const widthScale = scaleLinear().range([0, width]);
  return series.map(s => {
    const sum = s.values.reduce((a, b) => a + b.value, 0);
    widthScale.domain([0, sum]);
    let rectEnd = 0;
    const rects = s.values.map(subRect => {
      const start = rectEnd;
      rectEnd += widthScale(subRect.value);
      const end = rectEnd;
      return {
        ...subRect,
        start,
        end,
      };
    });
    return { title: s.title, rects };
  });
};

const GaugeChartContainer = styled.div`
  display: flex;
`;

const GaugeChartGraphContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const GaugeChartTitles = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: 16px;
  align-items: flex-end;
  h5 {
    font-size: 14px;
    color: #566072;
    font-weight: normal;
    letter-spacing: 0.28px;
  }
`;

const GaugeChartText = styled.text`
  font-weight: 600;
  font-size: 13px;
`;

const GaugeChartNumbersText = styled(GaugeChartText)`
  text-anchor: middle;
  dominant-baseline: middle;
  fill: white;
  color: white;
`;

const GaugeChartPercentageAxisText = styled(GaugeChartText)`
  dominant-baseline: text-before-edge;
  fill: #566072;
  color: #566072;
`;

const xAxisMarkers = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

const formatter = format(',');

export const HorizontalGaugeBarChart: FC<HorizontalGaugeBarChartProps> = props => {
  const graphContainer = useRef<HTMLDivElement>(null);
  const width = useResize(graphContainer, props.width);
  const rectSeries = useMemo(() => transformSeriesToRects(props.series, width), [
    props.series,
    width,
  ]);

  const height =
    2 * GAUGE_CHART_VERTICAL_PADDING +
    rectSeries.length * (GAUGE_CHART_RECT_PADDING + GAUGE_CHART_RECT_HEIGHT) -
    GAUGE_CHART_RECT_PADDING;
  return (
    <GaugeChartContainer>
      <GaugeChartTitles>
        {rectSeries.map((s, idx) => {
          const isFirst = idx === 0;
          const isLast = idx === rectSeries.length - 1;
          return (
            <h5
              key={s.title}
              style={{
                height: GAUGE_CHART_RECT_HEIGHT,
                margin: 0,
                marginTop: isFirst ? GAUGE_CHART_VERTICAL_PADDING : 0,
                marginBottom: isLast
                  ? GAUGE_CHART_VERTICAL_PADDING + GAUGE_CHART_RECT_HEIGHT
                  : GAUGE_CHART_RECT_PADDING,
              }}
            >
              {s.title}
            </h5>
          );
        })}
      </GaugeChartTitles>
      <GaugeChartGraphContainer ref={graphContainer}>
        <svg height={height} width="100%">
          <g>
            {xAxisMarkers.map((marker, idx) => {
              const isFirstOrLast = idx === 0 || idx === xAxisMarkers.length - 1;
              const xPosition = (marker * width) / 100;
              return (
                <VerticalLine
                  key={marker}
                  strokeWidth={isFirstOrLast ? '2px' : '1px'}
                  color="#e4e4e4"
                  height={height}
                  x={xPosition}
                />
              );
            })}
          </g>
          {rectSeries.map((series, idx) => {
            const y =
              (GAUGE_CHART_RECT_PADDING + GAUGE_CHART_RECT_HEIGHT) * idx +
              GAUGE_CHART_VERTICAL_PADDING;
            return (
              <g key={series.title}>
                {series.rects.map(r => {
                  return (
                    <g key={r.key}>
                      <rect
                        y={y}
                        x={r.start}
                        width={r.end - r.start}
                        fill={r.color}
                        height="25px"
                      />
                      <GaugeChartNumbersText y={y + 13} x={(r.start + r.end) / 2}>
                        {formatter(r.value)}
                      </GaugeChartNumbersText>
                    </g>
                  );
                })}
              </g>
            );
          })}
        </svg>
        <svg height={20} width="100%">
          <g>
            {xAxisMarkers
              .filter(marker => marker % 20 === 0)
              .map((marker, idx, arr) => {
                const isFirst = idx === 0;
                const isLast = idx === arr.length - 1;
                const textAnchor = isFirst ? 'start' : isLast ? 'end' : 'middle';
                return (
                  <GaugeChartPercentageAxisText
                    key={marker}
                    textAnchor={textAnchor}
                    y={1}
                    x={(marker * width) / 100}
                  >
                    {marker}%
                  </GaugeChartPercentageAxisText>
                );
              })}
          </g>
          GaugeChartPercentageAxisTexta
        </svg>
      </GaugeChartGraphContainer>
    </GaugeChartContainer>
  );
};
